import { useCallback, useEffect, useState } from 'react';
import { Bundle, Task, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { FollowUpTaskType, searchFollowUpTasks } from '../utils/fhir-helpers';
import { OrderSubState } from '../lims-types';
import { updateOrderSubState } from '../utils/order-substates-helper';

interface UseFollowUpTasksParams {
  diagnosticReportId: string | undefined;
  serviceRequest?: ServiceRequest;
  onServiceRequestUpdate?: (updatedSR: ServiceRequest) => void;
}

interface FollowUpTaskState {
  task: Task | null;
  type: FollowUpTaskType;
  completed: boolean;
}

interface UseFollowUpTasksReturn {
  tasks: FollowUpTaskState[];
  loading: boolean;
  toggle: (taskType: FollowUpTaskType) => Promise<void>;
  refresh: () => void;
}

export const useFollowUpTasks = ({
  diagnosticReportId,
  serviceRequest,
  onServiceRequestUpdate,
}: UseFollowUpTasksParams): UseFollowUpTasksReturn => {
  const medplum = useMedplum();
  const [tasks, setTasks] = useState<FollowUpTaskState[]>([]);
  const [loading, setLoading] = useState(false);

  const loadTasks = useCallback(async () => {
    if (!diagnosticReportId) {
      setTasks([]);
      return;
    }

    setLoading(true);
    try {
      const fhirTasks = await searchFollowUpTasks(medplum, diagnosticReportId);

      const taskStates: FollowUpTaskState[] = fhirTasks.map((t) => ({
        task: t,
        type: t.code?.coding?.[0]?.code as FollowUpTaskType,
        completed: t.status === 'completed',
      }));

      setTasks(taskStates);
    } catch (error) {
      console.error('Error loading follow-up tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [medplum, diagnosticReportId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const toggle = useCallback(
    async (taskType: FollowUpTaskType) => {
      const taskState = tasks.find((t) => t.type === taskType);
      if (!taskState?.task) {
        return;
      }

      try {
        const task = taskState.task;
        const newStatus =
          task.status === 'completed' ? 'requested' : 'completed';
        const updatedTask: Task = {
          ...task,
          status: newStatus,
          ...(newStatus === 'completed' && {
            lastModified: new Date().toISOString(),
          }),
        };

        const newTasks = tasks.map((t) =>
          t.type === taskType
            ? { ...t, task: updatedTask, completed: newStatus === 'completed' }
            : t
        );

        const bundleEntries: Bundle['entry'] = [
          {
            resource: updatedTask,
            request: { method: 'PUT', url: `Task/${task.id}` },
          },
        ];

        let updatedSR: ServiceRequest | null = null;
        if (serviceRequest?.id) {
          const allCompleted = newTasks.every((t) => t.completed);
          const anyCompleted = newTasks.some((t) => t.completed);

          const newSubState = allCompleted
            ? OrderSubState.Completed
            : anyCompleted
              ? OrderSubState.FollowUpTasks
              : null;

          if (newSubState) {
            const latestSR = await medplum.readResource(
              'ServiceRequest',
              serviceRequest.id
            );
            updatedSR = updateOrderSubState(latestSR, newSubState);
            bundleEntries.push({
              resource: updatedSR,
              request: {
                method: 'PUT',
                url: `ServiceRequest/${serviceRequest.id}`,
              },
            });
          }
        }

        const bundle: Bundle = {
          resourceType: 'Bundle',
          type: 'transaction',
          entry: bundleEntries,
        };

        await medplum.executeBatch(bundle);
        setTasks(newTasks);
        if (updatedSR) {
          onServiceRequestUpdate?.(updatedSR);
        }
      } catch (error) {
        console.error('Error toggling follow-up task:', error);
      }
    },
    [medplum, tasks, serviceRequest, onServiceRequestUpdate]
  );

  return { tasks, loading, toggle, refresh: loadTasks };
};
